import { Button, Flex, Radio, Stack, useToast } from '@chakra-ui/react'
import { DateFormControl, InputFormControl, RadioGroupFormControl, SelectFormControl } from 'BrokerTsLib/src/components'
import { SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'

import type { Step3FormValues } from '@/containers/steps/vehicle/Step3'
import { usePartner } from '@/context/PartnerContext'
import { Cloud } from '@/icons'

import { useFindCompany } from 'BrokerTsLib/src/hooks'
import { FormLabel } from '../../../../../../components/FormLabel'
import { useInsuranceParams } from '../../../../../../context/InsuranceParamsProvider'
import { useSelections } from '../../../../../../hooks'
import { FormBody } from '../../../../../forms/FormBody'
import { FormRow } from '../../../../../forms/FormRow'

export const VinkulantForm = () => {
  const { currentPartner } = usePartner()
  const { isDisabled } = useInsuranceParams()

  const isJap = currentPartner?.partner == 'Jap'

  const { watch, setValue, control } = useFormContext<Step3FormValues>()

  const { data: vinkulants, isLoading: isLoadingVinkulants } = useSelections(SelectionType.Vinkulant)
  const { data: vinkulantTypes, isLoading: isLoadingVinkulantTypes, isFetching: isFetchingVinkulantTypes } = useSelections(SelectionType.VinkulationType)

  const showVinkulant = watch('policyHolder.vinkulantExists')
  const creditorId = watch('policyHolder.vinkulant.creditorId')

  const fillJapVinkulant = (index: number) => {
    if (index == 1) { /*UniCredit*/
      setValue('policyHolder.vinkulant.creditorId', '00000263|15886492')
      setValue('policyHolder.vinkulant.creditorIc', '15886492')
      setValue('policyHolder.vinkulant.creditorCity', 'Praha 4 - Michle')
      setValue('policyHolder.vinkulant.creditorStreet', 'Želetavská 1525/1')
      setValue('policyHolder.vinkulant.creditorPostCode', '14000')
    }

    if (index == 2) { /*RCI*/
      setValue('policyHolder.vinkulant.creditorId', '00011591|25722328')
      setValue('policyHolder.vinkulant.creditorIc', '25722328')
      setValue('policyHolder.vinkulant.creditorCity', 'Praha 4 - Michle')
      setValue('policyHolder.vinkulant.creditorStreet', 'Želetavská 1525/1')
      setValue('policyHolder.vinkulant.creditorPostCode', '14000')
    }
  }

  const creditorIdChanged = (value: string) => {
    const valueIco = (value && value.indexOf('|') >= 0) ? value.substring(value.indexOf('|') + 1) : ''

    setValue('policyHolder.vinkulant.creditorIc', valueIco, { shouldValidate: true })
    setValue('policyHolder.vinkulant.creditorCity', '')
    setValue('policyHolder.vinkulant.creditorStreet', '')
    setValue('policyHolder.vinkulant.creditorPostCode', '')
  }

  const toast = useToast()
  const creditorIc = watch('policyHolder.vinkulant.creditorIc')
  const { mutateAsync: loadAres, isPending } = useFindCompany()
  const fillByIco = async () => {
    if (!creditorIc)
      return

    try {
      const data = await loadAres(creditorIc)

      setValue('policyHolder.vinkulant.creditorCity', data.address.city)
      setValue('policyHolder.vinkulant.creditorStreet', data.address.street + ' ' + data.address.streetNumber)
      setValue('policyHolder.vinkulant.creditorPostCode', data.address.postCode)
    }
    catch {
      toast({
        status: 'warning',
        title: 'IČO nenalezeno',
      })
    }
  }

  return (
    <>
      <FormBody>
        <FormRow>
          <RadioGroupFormControl name="policyHolder.vinkulantExists" control={control}
            isDisabled={isDisabled}
            label="Uvést vinkulanta"
            valueAsBoolean
          >
            <Stack direction="row" spacing={16}>
              <Radio value='true'>Ano</Radio>
              <Radio value='false'>Ne</Radio>
            </Stack>
          </RadioGroupFormControl>

          {isJap && showVinkulant && !isDisabled &&
            <Flex sx={{ gap: 8 }} >
              <FormLabel>Předvyplnit</FormLabel>
              <Button
                type="button"
                variant="solid"
                colorScheme="gray"
                onClick={() => fillJapVinkulant(1)}
              >
                UniCredit Leasing
              </Button>
              <Button
                type="button"
                variant="solid"
                colorScheme="gray"
                onClick={() => fillJapVinkulant(2)}
              >
                RCI Financial Services
              </Button>
            </Flex>
          }

        </FormRow>

        {showVinkulant &&
          <>
            <FormRow>
              <SelectFormControl name="policyHolder.vinkulant.creditorId" control={control}
                label="Vinkulant"
                isRequired
                isDisabled={isDisabled}
                options={vinkulants?.data.map(item => ({
                  value: item.value!,
                  label: item.description!
                }))}
                onChange={(v) => creditorIdChanged(v as string)}
                unselectedText="Vyberte"
                helperText={<>Vyberte název vinkulanta/leasingové společnosti. Pokud není v seznamu, vyberte položku "Jiný" a název uveďte do pole Bližší specifikace.</>}
                isLoading={isLoadingVinkulants}
              />

              <Flex gap={2} alignItems='start'>
                <InputFormControl name="policyHolder.vinkulant.creditorIc" isRequired control={control} isDisabled={isDisabled}
                  label="IČO"
                  flexGrow={1}
                />
                <Button
                  mt={8}
                  type="button"
                  variant="ghost"
                  isLoading={isPending}
                  isDisabled={isPending || !creditorIc || isDisabled}
                  onClick={() => fillByIco()}
                  padding={2.5}
                >
                  <Cloud color="secondary.500" />
                </Button>
              </Flex>

            </FormRow>

            <FormRow>
              {creditorId == '00000035' &&
                <InputFormControl name="policyHolder.vinkulant.creditorName" control={control} isDisabled={isDisabled}
                  label="Bližší specifikace"
                  helperText="Zde zadejte název vinkulanta/leasingové společnosti, pokud není v seznamu"
                />
              }

              <SelectFormControl name="policyHolder.vinkulant.vinkulationType" control={control}
                label="Typ vinkulace"
                options={vinkulantTypes?.data.map(item => ({
                  value: item.value!,
                  label: item.description!
                }))}
                isDisabled={isDisabled}
                unselectedText="Vyberte"
                isLoading={isLoadingVinkulantTypes || isFetchingVinkulantTypes}
              />
            </FormRow>

            <FormRow>
              <InputFormControl name="policyHolder.vinkulant.policyNumber" control={control} isDisabled={isDisabled}
                label="Číslo smlouvy"
              />
            </FormRow>

            <FormRow>
              <DateFormControl name="policyHolder.vinkulant.policyStartDate" control={control}
                label="Datum počátku smlouvy"
                isDisabled={isDisabled}
              />

              <DateFormControl name="policyHolder.vinkulant.policyEndDate" control={control}
                label="Datum konce smlouvy"
                isDisabled={isDisabled}
              />
            </FormRow>

            <FormRow itemsCount={3}>
              <InputFormControl name="policyHolder.vinkulant.creditorCity" control={control} isDisabled={isDisabled}
                label="Obec"
              />

              <InputFormControl name="policyHolder.vinkulant.creditorStreet" control={control} isDisabled={isDisabled}
                label="Ulice a číslo"
              />

              <InputFormControl name="policyHolder.vinkulant.creditorPostCode" control={control} isDisabled={isDisabled}
                label="PSČ"
                htmlSize={6}
              />
            </FormRow>
          </>
        }

      </FormBody>
    </>
  )
}